import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import { apiFetch } from '../utils/api';
import { FigureCoWordmark, FigureCoMonogram } from './brand/FigureCoLogo';
import {
  Home,
  FileText,
  Bell,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  Menu,
  X,
  MessageSquare,
  PlusCircle,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Mail
} from 'lucide-react';

interface AccountantLayoutProps {
  children: React.ReactNode;
}

export default function AccountantLayout({ children }: AccountantLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountant, logout } = useAccountantAuth();
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notifications (pending invites + unread comments)
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!accountant) return;
      
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('accountant_token')}` };
        
        const notifs: any[] = [];
        
        // Fetch pending invites using api utility
        try {
          const invites = await apiFetch<any[]>(
            '/api/v1/collaboration/invites/pending',
            { headers, showErrorToast: false }
          );
          invites.forEach((invite: any) => {
            notifs.push({
              id: `invite-${invite.report_id}`,
              type: 'invite',
              title: 'New Report Shared',
              message: `${invite.shared_by_name} shared ${invite.client_name}`,
              time: new Date(invite.shared_at),
              reportId: invite.report_id
            });
          });
        } catch {
          // Silent fail for notifications
        }
        
        // Fetch dashboard stats for unread comments count
        try {
          const stats = await apiFetch<{ unread_comments: number }>(
            '/api/v1/collaboration/accountant/dashboard-stats',
            { headers, showErrorToast: false }
          );
          if (stats.unread_comments > 0) {
            notifs.push({
              id: 'unread-comments',
              type: 'comment',
              title: 'Unread Comments',
              message: `You have ${stats.unread_comments} unread comment${stats.unread_comments > 1 ? 's' : ''}`,
              time: new Date(),
              path: '/accountant/comments'
            });
          }
        } catch {
          // Silent fail for notifications
        }
        
        notifs.sort((a, b) => b.time.getTime() - a.time.getTime());
        setNotifications(notifs);
        setNotificationCount(notifs.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    
    fetchNotifications();
  }, [accountant]);

  const navItems = [
    { icon: Home, label: 'Home', path: '/accountant/home' },
    { icon: FileText, label: 'Shared Reports', path: '/accountant/reports' },
    { icon: MessageSquare, label: 'Comments', path: '/accountant/comments' },
  ];

  const bottomNavItems = [
    { icon: User, label: 'Profile', path: '/accountant/profile' },
  ];

  const isActive = (path: string) => {
    // External-portal home. /accountant and /accountant/dashboard redirect
    // to /accountant/reports in App.tsx, so they also highlight "home".
    if (path === '/accountant/reports') {
      return location.pathname === '/accountant/reports' ||
             location.pathname === '/accountant' ||
             location.pathname === '/accountant/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/accountant/login');
  };

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[var(--color-bg-base)] text-[var(--color-text-primary)] transition-all duration-300 flex flex-col h-full
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-[var(--color-hairline)]">
        <div 
          className="flex items-center justify-center cursor-pointer"
          onClick={() => navigate('/accountant/reports')}
          title="Go to Dashboard"
        >
          {/* The Figure Co. logo (code, theme-aware). Monogram when collapsed;
              the lockup's own tagline is suppressed so "Accountant Portal" reads
              as the surface label beneath it. */}
          {sidebarCollapsed && !mobile ? (
            <span className="flex items-center justify-center py-1 text-text-primary hover:opacity-80 transition-opacity">
              <FigureCoMonogram size={32} />
            </span>
          ) : (
            <div className="flex flex-col items-center py-2 hover:opacity-80 transition-opacity">
              <FigureCoWordmark size="md" tagline={false} />
              <span
                className="font-sans mt-2 text-[0.625rem] uppercase tracking-[0.18em]"
                style={{ color: 'var(--color-text-muted)' }}
              >
                Accountant Portal
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {/* Create New Claim - Prominent Link to Main Dashboard */}
        {/* /wizard-v2 is Clerk-gated: an accountant-JWT session would be bounced to sign-in. */}
        {!accountant && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate('/wizard-v2', { state: { forceNew: true } })}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all shadow-lg"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            <PlusCircle className="w-[18px] h-[18px] flex-shrink-0" />
            {(!sidebarCollapsed || mobile) && (
              <>
                <span className="font-medium flex-1">Create New Claim</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </>
            )}
          </button>
        </div>
        )}
        
        <div className="space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                setMobileMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive(item.path)
                  ? 'bg-[var(--color-brand)] text-[var(--color-on-brand)] shadow-lg'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              {(!sidebarCollapsed || mobile) && <span className="font-medium">{item.label}</span>}
            </button>
          ))}
        </div>
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && accountant && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-hover)]">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--color-brand)' }}>
              <span className="text-[var(--color-on-brand)] text-xs font-bold">
                {accountant.full_name?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              {/* AccountantProfile field is `full_name` not `name` —
                  consumer had drifted (TS-FU3 batch 4). */}
              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{accountant.full_name}</p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">{accountant.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] rounded-lg text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}

      {/* Collapse Toggle */}
      {!mobile && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)] rounded-lg transition"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar mobile />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 p-2 text-[var(--color-text-primary)] bg-[var(--color-bg-elevated)] rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="border-b px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-40" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold hidden sm:block" style={{ color: 'var(--color-text-primary)' }}>
              Accountant Portal
            </h1>
          </div>

          <div className="flex items-center gap-3">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="relative p-2 rounded-lg"
                style={{ color: 'var(--color-text-dim)' }}
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />
                )}
              </button>

              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 rounded-xl shadow-lg border z-50" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--color-text-ghost)' }} />
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => {
                            if (notif.path) {
                              navigate(notif.path);
                            } else if (notif.reportId) {
                              navigate(`/accountant/reports/${notif.reportId}`);
                            }
                            setNotificationDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 border-b text-left flex items-start gap-3"
                          style={{ borderColor: 'var(--color-border-faint)' }}
                        >
                          <div
                            className="p-1.5 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: notif.type === 'comment' ? 'transparent' : 'var(--color-needs-human-tint)'
                            }}
                          >
                            {notif.type === 'invite' ? (
                              <Mail className="w-4 h-4 text-[var(--color-brand)]" />
                            ) : notif.type === 'comment' ? (
                              <MessageSquare className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                            ) : (
                              <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{notif.title}</p>
                            <p className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>{notif.message}</p>
                            <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                              {new Date(notif.time).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                      <button
                        onClick={() => {
                          setNotifications([]);
                          setNotificationCount(0);
                          setNotificationDropdownOpen(false);
                        }}
                        className="text-xs text-[var(--color-brand)]"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
